
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import SubmissionDetailModal from "@/components/admin/SubmissionDetailModal";
import PageAccessCard from "@/components/admin/PageAccessCard";
import QuickActions from "@/components/admin/dashboard/QuickActions";
import AdminSections from "@/components/admin/dashboard/AdminSections";
import RecentActivity from "@/components/admin/dashboard/RecentActivity";
import AnalyticsCard from "@/components/admin/dashboard/AnalyticsCard";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [printerSuggestions, setPrinterSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [deletingRejected, setDeletingRejected] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    pendingSubmissions: 0,
    approvedSubmissions: 0,
    rejectedSubmissions: 0,
    pendingPrinterSuggestions: 0,
    approvedPrinterSuggestions: 0,
    rejectedPrinterSuggestions: 0,
    totalComponents: 156,
    totalUsers: 342,
    totalManufacturers: 0,
    totalPrinters: 0
  });

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching submissions...');
      
      const { data, error } = await supabase
        .from("submissions")
        .select("*")
        .order("submitted_at", { ascending: false });
      
      if (error) {
        console.error('Submissions query error:', error);
        toast.error(`Error loading submissions: ${error.message}`);
        return;
      }
      
      console.log('📋 Submissions fetched:', data?.length || 0, 'items');
      setSubmissions(data || []);
      
      // Update submission stats
      const pending = data?.filter(s => s.status === 'pending').length || 0;
      const approved = data?.filter(s => s.status === 'approved').length || 0;
      const rejected = data?.filter(s => s.status === 'rejected').length || 0;
      
      setDashboardStats(prev => ({
        ...prev,
        pendingSubmissions: pending,
        approvedSubmissions: approved,
        rejectedSubmissions: rejected
      }));
      
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrinterSuggestions = async () => {
    try {
      console.log('🏭 Fetching printer manufacturer suggestions...');
      
      const { data, error } = await supabase
        .from("printer_manufacturer_suggestions")
        .select("*")
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error('Printer suggestions query error:', error);
        return;
      }
      
      console.log('🏭 Printer suggestions fetched:', data?.length || 0, 'items');
      setPrinterSuggestions(data || []);
      
      // Update printer suggestion stats
      const pending = data?.filter(s => s.status === 'pending').length || 0;
      const approved = data?.filter(s => s.status === 'approved').length || 0;
      const rejected = data?.filter(s => s.status === 'rejected').length || 0;
      
      setDashboardStats(prev => ({
        ...prev,
        pendingPrinterSuggestions: pending,
        approvedPrinterSuggestions: approved,
        rejectedPrinterSuggestions: rejected
      }));
      
    } catch (error) {
      console.error('Error fetching printer suggestions:', error);
    }
  };

  const fetchManufacturerPrinterStats = async () => {
    try {
      // Fetch manufacturer count
      const { count: manufacturerCount, error: manufacturerError } = await supabase
        .from("manufacturers")
        .select("*", { count: 'exact', head: true });

      // Fetch printer count
      const { count: printerCount, error: printerError } = await supabase
        .from("printers")
        .select("*", { count: 'exact', head: true });

      if (!manufacturerError && !printerError) {
        setDashboardStats(prev => ({
          ...prev,
          totalManufacturers: manufacturerCount || 0,
          totalPrinters: printerCount || 0
        }));
      }
    } catch (error) {
      console.error('Error fetching manufacturer/printer stats:', error);
    }
  };

  const handleBulkDeleteRejected = async () => {
    try {
      setDeletingRejected(true);
      console.log('🗑️ Deleting all rejected submissions...');
      
      const { error } = await supabase
        .from("submissions")
        .delete()
        .eq("status", "rejected");
      
      if (error) {
        console.error('Error deleting rejected submissions:', error);
        toast.error(`Failed to delete rejected submissions: ${error.message}`);
        return;
      }
      
      toast.success('All rejected submissions have been deleted');
      fetchSubmissions(); // Refresh the data
      
    } catch (error) {
      console.error('Error in bulk delete:', error);
      toast.error('Failed to delete rejected submissions');
    } finally {
      setDeletingRejected(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    fetchPrinterSuggestions();
    fetchManufacturerPrinterStats();
  }, []);

  const handleSubmissionAction = async (submissionId, action, feedback = null) => {
    try {
      const updateData = {
        status: action,
        feedback: feedback || null,
        approved_by: action === "approved" ? user?.id : null,
        approved_at: action === "approved" ? new Date().toISOString() : null
      };

      const { error } = await supabase
        .from("submissions")
        .update(updateData)
        .eq("id", submissionId);

      if (error) throw error;

      toast.success(`Submission ${action} successfully`);
      fetchSubmissions(); // Refresh the data
      setSelectedSubmission(null);

    } catch (error) {
      console.error(`Error ${action}ing submission:`, error);
      toast.error(`Failed to ${action} submission`);
    }
  };

  const totalPendingCount = dashboardStats.pendingSubmissions + dashboardStats.pendingPrinterSuggestions;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header useBackButton={true} pageName="Admin Dashboard" />
      
      <div className="container py-8 pt-[64px] space-y-8">
        {/* Dashboard Overview */}
        <div>
          <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Manage submissions, components, users, and platform settings
          </p>
        </div>

        {/* Quick Actions */}
        <QuickActions pendingCount={totalPendingCount} />

        {/* Site Management */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Site Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <PageAccessCard />
            <AnalyticsCard />
          </div>
        </div>

        {/* Admin Sections */}
        <AdminSections dashboardStats={dashboardStats} />

        {/* Recent Activity */}
        <RecentActivity
          submissions={submissions}
          printerSuggestions={printerSuggestions}
          rejectedCount={dashboardStats.rejectedSubmissions}
          loading={loading}
          deletingRejected={deletingRejected}
          onBulkDeleteRejected={handleBulkDeleteRejected}
          onViewSubmission={setSelectedSubmission}
          navigate={navigate}
        />
      </div>

      {/* Submission Detail Modal */}
      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          isOpen={!!selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onAction={handleSubmissionAction}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
